// Card Cyclone Effect
// A high-fidelity, ultra-premium animation for the "Card Cyclone" card.
// Narrative: A massive vortex of glowing, spinning cards forms, pulling and spinning faster until 3 cards shoot into the player's hand.
// Breakdown:
// 1. The Gathering Wind (0 - 800ms): High-speed dust and wind particles start spinning.
// 2. The Cyclone (800 - 3000ms): Floating cards (using the actual back.png) get sucked into the vortex, spinning in pseudo-3D. Heavy blur and composite blending.
// 3. The Draw (3000 - 4500ms): The cyclone bursts outwards, 3 glowing cards shoot rapidly into the player's hand.
// 4. Dissipation (4500 - 5000ms): The wind dies down, and residual dust fades away.

const CONFIG = {
    duration: 5000,
    windGather: 800,
    cycloneHold: 3000,
    numCardsCyclone: 28, // Reduced by 20% (from 35)
    numCardsDrawn: 3,
    baseCardScale: 0.15,
};

// --- Cache for the Card Back Image ---
let cardBackImage: HTMLImageElement | null = null;
let imageLoaded = false;

function loadCardBackImage() {
    if (cardBackImage) return;
    cardBackImage = new Image();
    cardBackImage.src = '/assets/cards/classic/back.png';
    cardBackImage.onload = () => { imageLoaded = true; };
}



class CycloneCard {
    angle: number; radius: number; y: number; speed: number;
    rotX: number; rotY: number; rotZ: number;
    rxSpeed: number; rySpeed: number; rzSpeed: number;
    colorGlow: string;

    constructor(cx: number, cy: number) {
        this.angle = Math.random() * Math.PI * 2;
        this.radius = Math.random() * 400 + 100;
        this.y = cy + (Math.random() - 0.5) * 400;
        this.speed = 0.01 + Math.random() * 0.03;

        this.rotX = Math.random() * Math.PI * 2;
        this.rotY = Math.random() * Math.PI * 2;
        this.rotZ = Math.random() * Math.PI * 2;

        this.rxSpeed = (Math.random() - 0.5) * 0.2;
        this.rySpeed = (Math.random() - 0.5) * 0.2;
        this.rzSpeed = (Math.random() - 0.5) * 0.2;

        const colors = ['#00ffff', '#ff00ff', '#ffff00', '#00ffaa'];
        this.colorGlow = colors[Math.floor(Math.random() * colors.length)];
    }

    update(cx: number, cy: number, dt: number, globalTime: number) {
        if (globalTime < CONFIG.cycloneHold) {
            // tighten vortex
            this.radius += (40 + Math.random() * 20 - this.radius) * 0.05 * dt * 0.05;
            this.speed = 0.05 + (200 / Math.max(20, this.radius)) * 0.05;
            this.y += (cy - this.y) * 0.02 * dt * 0.05;
        } else {
            // chaotic scatter
            this.radius += 8 * dt * 0.05;
            this.speed *= 0.9;
        }

        this.angle += this.speed * dt * 0.05;
        this.rotX += this.rxSpeed * dt * 0.05;
        this.rotY += this.rySpeed * dt * 0.05;
        this.rotZ += this.rzSpeed * dt * 0.05;
    }

    draw(ctx: CanvasRenderingContext2D, alpha: number, cx: number) {
        const px = cx + Math.cos(this.angle) * this.radius;
        const z = Math.sin(this.angle);

        // Don't draw if it's too far behind and we are fading (save performance / look better)
        if (z < -0.8 && alpha < 0.5) return;

        // Base width and height of the procedural card
        const baseW = 100 * CONFIG.baseCardScale;
        const baseH = 150 * CONFIG.baseCardScale;

        const scale = 0.8 + (z + 1) * 0.4;

        ctx.save();
        ctx.globalAlpha = alpha * (0.4 + (z + 1) * 0.3); // Fade out behind // Fade out behind

        ctx.translate(px, this.y);

        // Pseudo 3D rotation using scale and rotate
        // rotY simulates flipping horizontally
        const flipY = Math.cos(this.rotY);
        // rotX simulates flipping vertically
        const flipX = Math.cos(this.rotX);

        ctx.rotate(this.rotZ);
        ctx.scale(scale * flipY, scale * flipX);

        const w = baseW;
        const h = baseH;

        // Neon Glow Style from Broken Secret
        ctx.shadowBlur = 15;
        ctx.shadowColor = this.colorGlow;
        ctx.fillStyle = this.colorGlow;

        // Draw the colored backing
        ctx.fillRect(-w / 2, -h / 2, w, h);

        // Draw the intense white core
        ctx.fillStyle = '#ffffff';
        const padding = 2; // thin colored border
        ctx.fillRect(-w / 2 + padding, -h / 2 + padding, w - padding * 2, h - padding * 2);

        ctx.restore();
    }
}

class DrawnCard {
    x: number; y: number; startX: number; startY: number; targetX: number; targetY: number;
    rot: number; delay: number; life: number; color: string;

    constructor(startX: number, startY: number, targetX: number, targetY: number, delayMs: number) {
        this.startX = startX; this.startY = startY;
        this.targetX = targetX; this.targetY = targetY;
        this.x = startX; this.y = startY;
        this.rot = Math.random() * Math.PI * 2;
        this.delay = delayMs;
        this.life = 0; // 0 to 1
        this.color = '#ffffff';
    }

    update(dt: number, globalTime: number) {
        if (globalTime < this.delay) return;

        this.life += dt * 0.0015; // travel speed
        if (this.life > 1) this.life = 1;

        // Easing function (Ease In Out Cubic)
        const t = this.life;
        const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

        // Add an arc to the path
        const arcHeight = 200 * Math.sin(t * Math.PI);

        this.x = this.startX + (this.targetX - this.startX) * ease;
        this.y = this.startY + (this.targetY - this.startY) * ease - arcHeight;

        // Normalize rotation to 0 when it reaches the hand
        this.rot = this.rot * (1 - ease);
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0 || !imageLoaded || !cardBackImage) return;

        ctx.save();

        // Fade out slightly right as it touches the hand
        ctx.globalAlpha = this.life > 0.9 ? (1 - this.life) * 10 : 1;

        ctx.translate(this.x, this.y);

        // As it approaches target, scale from cyclone size (0.15) to almost full size (0.3)
        const scale = CONFIG.baseCardScale + (this.life * 0.15);
        ctx.scale(scale, scale);
        ctx.rotate(this.rot);

        const w = cardBackImage.width;
        const h = cardBackImage.height;

        // Premium Glow
        ctx.shadowBlur = 40;
        ctx.shadowColor = '#00ffff';

        ctx.drawImage(cardBackImage, -w / 2, -h / 2);

        // Flash of light on top
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = `rgba(150, 255, 255, ${Math.sin(this.life * Math.PI)})`;
        ctx.fillRect(-w / 2, -h / 2, w, h);

        ctx.restore();
    }
}


// --- Effect State ---
export type CardCycloneEffect = {
    kind: 'cardCyclone';
    t: number;
    tx: number; // Target X (Usually Hand position)
    ty: number; // Target Y (Usually Hand position)
    opp: boolean; // Is Opponent?
    data?: {
        cycloneCards: CycloneCard[];
        drawnCards: DrawnCard[];
        cx: number;
        cy: number;
        lastT: number;
    }
}

export const createCardCycloneEffect = (tx: number, ty: number, opp: boolean): CardCycloneEffect => ({
    kind: 'cardCyclone', t: 0, tx, ty, opp
});

export const renderCardCycloneEffect = (
    eff: CardCycloneEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    loadCardBackImage();

    if (eff.t >= CONFIG.duration) return false;

    const { width, height } = ctx.canvas;

    // Vortex center
    const cx = width / 2;
    // slightly above center for player, below for opp (deck area visual approximation)
    const cy = eff.opp ? height * 0.3 : height * 0.7;

    if (!eff.data) {
        eff.data = {
            cycloneCards: Array.from({ length: CONFIG.numCardsCyclone }, () => new CycloneCard(cx, cy)),
            drawnCards: [],
            cx, cy,
            lastT: Date.now()
        };

        // Setup the 3 drawn cards
        // They delay until Phase 3 (CONFIG.cycloneHold)
        for (let i = 0; i < CONFIG.numCardsDrawn; i++) {
            // slight offset for each card in hand
            const spreadX = eff.opp ? (i - 1) * -80 : (i - 1) * 80;
            const targetX = eff.tx + spreadX;
            // Delay them slightly so they fly out one after another
            eff.data.drawnCards.push(new DrawnCard(cx, cy, targetX, eff.ty, CONFIG.cycloneHold + i * 200));
        }
    }

    const dt = Date.now() - eff.data.lastT;
    eff.data.lastT = Date.now();
    const d = eff.data;

    ctx.save();

    // Calculate cyclone opacity
    let cycloneAlpha = 1;
    if (eff.t < CONFIG.windGather) cycloneAlpha = eff.t / CONFIG.windGather;
    if (eff.t > CONFIG.cycloneHold) cycloneAlpha = 1 - (eff.t - CONFIG.cycloneHold) / (CONFIG.duration - CONFIG.cycloneHold);

    // 1. Soft milky-beige tornado light effect
    let tornadoAlpha = 0;
    if (eff.t < CONFIG.windGather) {
        tornadoAlpha = (eff.t / CONFIG.windGather);
    } else if (eff.t < CONFIG.cycloneHold) {
        tornadoAlpha = 1;
    } else {
        tornadoAlpha = Math.max(0, 1 - (eff.t - CONFIG.cycloneHold) / (CONFIG.duration - CONFIG.cycloneHold));
    }

    if (tornadoAlpha > 0.01) {
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';

        // Default base sits exactly on the avatar center
        const tornadoHeight = 500;

        // Shift it by half its size towards the avatar so the avatar is submerged in it
        const shiftY = eff.opp ? -tornadoHeight * 0.5 : tornadoHeight * 0.5;
        const baseCy = cy + shiftY;

        // It extends away towards the edge of the screen
        const tornadoTop = eff.opp ? baseCy + tornadoHeight : baseCy - tornadoHeight;

        const widthAtTop = 300;
        const widthAtBottom = 20; // Thin at the avatar

        // --- 1. Draw the glowing body with a vertical fade to hide harsh edges ---
        // Create a mask/fade using globalAlpha and a vertical gradient
        // We do this by drawing the shape with a solid horizontal gradient, 
        // but multiplying it by a vertical gradient (simulated via globalAlpha or another composite layer if complex, 
        // but here we just use vertical gradient for the fill directly)

        ctx.beginPath();
        // Start at top left
        ctx.moveTo(cx - widthAtTop, tornadoTop);

        // Curve down to bottom left
        const cp1y = eff.opp ? baseCy + tornadoHeight * 0.5 : baseCy - tornadoHeight * 0.5;
        ctx.bezierCurveTo(
            cx - widthAtTop * 0.5, cp1y,
            cx - widthAtBottom * 0.5, baseCy - (eff.opp ? -tornadoHeight * 0.2 : tornadoHeight * 0.2),
            cx - widthAtBottom, baseCy
        );

        // Across the bottom
        ctx.lineTo(cx + widthAtBottom, baseCy);

        // Curve up to top right
        ctx.bezierCurveTo(
            cx + widthAtBottom * 0.5, baseCy - (eff.opp ? -tornadoHeight * 0.2 : tornadoHeight * 0.2),
            cx + widthAtTop * 0.5, cp1y,
            cx + widthAtTop, tornadoTop
        );
        ctx.closePath();

        // Base Horizontal Gradient (Milky beige)
        const hGrad = ctx.createLinearGradient(cx - widthAtTop, 0, cx + widthAtTop, 0);
        const baseAlpha = 0.5 * tornadoAlpha;
        const pulse = 1 + Math.sin(eff.t * 0.01) * 0.15;

        hGrad.addColorStop(0, `rgba(245, 245, 220, 0)`);
        hGrad.addColorStop(0.3, `rgba(245, 245, 220, ${0.1 * baseAlpha * pulse})`);
        hGrad.addColorStop(0.5, `rgba(255, 255, 240, ${0.6 * baseAlpha * pulse})`);
        hGrad.addColorStop(0.7, `rgba(245, 245, 220, ${0.1 * baseAlpha * pulse})`);
        hGrad.addColorStop(1, `rgba(245, 245, 220, 0)`);

        // We clip the context to the tornado shape
        ctx.save();
        ctx.clip();

        // Fill the whole bounding box, but with a VERTICAL gradient so the top and bottom fade out perfectly
        const vGrad = ctx.createLinearGradient(0, baseCy, 0, tornadoTop);
        // Base (at avatar) is transparent so it doesn't hard-cut the avatar
        vGrad.addColorStop(0, `rgba(255, 255, 255, 0)`);
        // Just above avatar it gets intense
        vGrad.addColorStop(0.1, `rgba(255, 255, 255, 1)`);
        // Middle is solid
        vGrad.addColorStop(0.7, `rgba(255, 255, 255, 0.8)`);
        // Top fades out into the sky/deck
        vGrad.addColorStop(1, `rgba(255, 255, 255, 0)`);

        ctx.fillStyle = hGrad;
        ctx.fillRect(cx - widthAtTop, Math.min(baseCy, tornadoTop), widthAtTop * 2, tornadoHeight);

        // Apply vertical fade mask
        ctx.globalCompositeOperation = 'destination-in';
        ctx.fillStyle = vGrad;
        ctx.fillRect(cx - widthAtTop, Math.min(baseCy, tornadoTop), widthAtTop * 2, tornadoHeight);
        ctx.restore(); // remove clip

        // --- 2. Swirling Rings ---
        ctx.globalCompositeOperation = 'lighter';
        const numRings = 5;
        for (let r = 1; r <= numRings; r++) {
            const hRatio = r / (numRings + 1);
            // ringY goes from baseCy towards tornadoTop
            const ringY = eff.opp ? baseCy + tornadoHeight * hRatio : baseCy - tornadoHeight * hRatio;

            const wRatio = Math.pow(hRatio, 2);
            const ringRadX = widthAtBottom + (widthAtTop - widthAtBottom) * wRatio * 0.8;
            const ringRadY = ringRadX * 0.2;

            // Fade rings at the very top/bottom matching the vertical gradient
            let ringFade = 1;
            if (hRatio < 0.1) ringFade = hRatio * 10;
            if (hRatio > 0.8) ringFade = 1 - (hRatio - 0.8) * 5;

            ctx.beginPath();
            ctx.ellipse(cx, ringY, ringRadX, ringRadY, 0, 0, Math.PI * 2);
            ctx.lineWidth = 15;
            ctx.setLineDash([40, 60]);
            ctx.lineDashOffset = -eff.t * (0.1 + hRatio * 0.1);
            ctx.strokeStyle = `rgba(255, 255, 240, ${0.1 * baseAlpha * pulse * ringFade})`;
            ctx.stroke();
            ctx.setLineDash([]);
        }
        ctx.restore();
    }

    // Sort cards by Z-axis to draw back-to-front (Pseudo-3D)
    // z = Math.sin(angle). Smaller Z means further back.
    d.cycloneCards.sort((a, b) => Math.sin(a.angle) - Math.sin(b.angle));

    // Calculate cyclone opacity (already defined above)

    // Update & Draw Vortex Cards
    d.cycloneCards.forEach(card => {
        card.update(cx, cy, dt, eff.t);
        // Heavy motion blur simulation via multiple draws with trailing angles
        ctx.globalAlpha = 0.3 * cycloneAlpha;
        const originalAngle = card.angle;
        for (let i = 0; i < 3; i++) {
            card.angle = originalAngle - i * 0.1 * card.speed;
            card.draw(ctx, cycloneAlpha, cx);
        }
        card.angle = originalAngle; // restore
        card.draw(ctx, cycloneAlpha, cx);
    });

    // Draw the 3 Flying Cards
    ctx.globalCompositeOperation = 'source-over'; // Normal blending for the actual drawn cards
    d.drawnCards.forEach(dc => {
        dc.update(dt, eff.t);
        dc.draw(ctx);
    });

    ctx.restore();
    return true;
};
